import { useEffect, useMemo, useState } from "react";
import { Modal } from "../../../../../../components/ui/Modal/index.js";
import type { Plan } from "../../types.js";
import { useGetPlaneCustomer } from "../../../../../../core/api/customer/revenues/queries.js";
import { cn } from "../../../../../../lib/cn.js";
import Radio from "../../../../../../components/ui/Button/radio.js";
import Icon from "../../../../../../components/ui/Icon/index.js";

// ============================================================
// Types
// ============================================================
interface ModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSelectPlan: (plan: Plan) => void;
    selectedPlanId?: number | null;
    selectedPlan?: any; // ✅ آبجکت طرح انتخاب‌شده (اختیاری)
    title: string;
    des: string;
}

// ============================================================
// Plan Image
// ============================================================
const getPlanImage = (type?: string) => {
    switch (type) {
        case "INTEREST":
            return "/app/assets/images/DiamondInterest.webp";
        case "LOAN":
            return "/app/assets/images/LoansAndInterest.webp";
        case "BOTH":
            return "/app/assets/images/LoanInterest.webp";
        case "BENEFIT":
            return "/app/assets/images/streetCardDefault.webp";
        case "ALL":
            return "/app/assets/images/LoanInterest.webp";
        default:
            return "/app/assets/images/streetCardDefault.webp";
    }
};

// ============================================================
// Plan Tag
// ============================================================
const getPlanTag = (type?: string) => {
    switch (type) {
        case "INTEREST":
            return {
                label: "بیشترین سود",
                className: "bg-sky-50 text-sky-700",
                Icon: "diamond-outline",
            };
        case "LOAN":
            return {
                label: "مناسب دریافت وام",
                className: "bg-green-50 text-green-700",
                Icon: "griddy-icons",
            };
        case "BOTH":
            return {
                label: "ترکیبی از وام و سود",
                className: "bg-purple-50 text-purple-700",
                Icon: "loandash-dark",
            };
        case "BENEFIT":
            return {
                label: "بن کارت",
                className: "bg-amber-50 text-amber-700",
                Icon: "loandash-dark",
            };
        case "ALL":
            return {
                label: "سود، وام و بن کارت",
                className: "bg-purple-50 text-purple-700",
                Icon: "loandash-dark",
            };
        default:
            return {
                label: "نامشخص",
                className: "bg-neutral-50 text-neutral-600",
                Icon: "diamond-outline",
            };
    }
};

// ============================================================
// Main Component
// ============================================================
const SelectPlanModal: React.FC<ModalProps> = ({
    isOpen,
    onClose,
    onSelectPlan,
    selectedPlanId,
    selectedPlan,
    title,
    des,
}) => {
    const { data, isLoading, isError } = useGetPlaneCustomer();

    // ✅ نرمال‌سازی payload
    const plansArray: any[] = useMemo(() => {
        if (!data) return [];
        if (Array.isArray(data?.payload)) return data.payload;
        if (Array.isArray(data)) return data;
        return [];
    }, [data]);

    // ✅ اگه selectedPlan از والد پاس داده شده و توی لیست نیست، اضافه‌ش کن
    const displayPlans: any[] = useMemo(() => {
        const all = [...plansArray];
        if (
            selectedPlan?.id != null &&
            !all.some((p: any) => String(p.id) === String(selectedPlan.id))
        ) {
            all.unshift(selectedPlan);
        }
        return all;
    }, [plansArray, selectedPlan]);

    // ✅ تبدیل داده API به Plan
    const mapApiDataToPlan = (item: any): any => {
        return {
            id: item?.id,
            title: item?.name || item?.title || "",
            name: item?.name || item?.title || "", // ✅ برای سازگاری
            description: item?.description || "",
            firstPercent:
                item?.customerInterest ?? item?.firstPercent ?? 0,
            secondPercent:
                item?.customerLoan ?? item?.secondPercent ?? 0,
            customerInterest:
                item?.customerInterest ?? item?.firstPercent ?? 0,
            customerLoan: item?.customerLoan ?? item?.secondPercent ?? 0,
            imageUrl: getPlanImage(item?.type),
            type: item?.type,
            visitorInterest: item?.visitorInterest,
            visitorLoan: item?.visitorLoan,
            activeForVisitor: item?.activeForVisitor,
            specificPlan: item?.specificPlan,
        };
    };

    const handlePlanSelect = (plan: any) => {
        onSelectPlan(plan);
        onClose();
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={title}
            description={des}
            headerVariant="center"
            rounded="2xl"
            size="auto"
            overlayBlur
        >
            <div className="flex flex-col gap-4">
                {/* Loading */}
                {isLoading && (
                    <div className="flex items-center justify-center py-8">
                        <div className="w-8 h-8 border-2 rounded-full animate-spin border-brand-500 border-t-transparent" />
                    </div>
                )}

                {/* Error */}
                {isError && (
                    <div className="py-8 text-sm text-center text-red-500">
                        خطا در دریافت طرح‌ها
                    </div>
                )}

                {/* Plans */}
                {!isLoading &&
                    !isError &&
                    displayPlans.map((item: any) => {
                        const plan = mapApiDataToPlan(item);
                        const isSelected =
                            selectedPlanId != null &&
                            String(selectedPlanId) === String(plan.id);
                        const tag = getPlanTag(plan.type);

                        return (
                            <div
                                key={plan.id}
                                onClick={() => handlePlanSelect(plan)}
                                className={cn(
                                    "flex w-full justify-between items-center gap-3 p-2 rounded-2xl border transition cursor-pointer",
                                    isSelected
                                        ? "bg-brand-50 border-brand-600"
                                        : "border-neutral-200 hover:border-neutral-300"
                                )}
                            >
                                <Radio
                                    id={`plan-${plan.id}`}
                                    name="plan"
                                    checked={isSelected}
                                    onChange={() => handlePlanSelect(plan)}
                                    size="lg"
                                />

                                <div className="w-full flex flex-col justify-between py-1 gap-2 text-neutral-700">
                                    <div className="w-full flex justify-between items-start">
                                        <div className="flex w-full justify-between items-center gap-1">
                                            <h5 className="text-base font-bold text-brand-900">
                                                {plan.title || "بدون نام"}
                                            </h5>
                                            <div
                                                className="text-sm font-bold text-brand-800"
                                                dir="ltr"
                                            >
                                                %{plan.secondPercent}-%
                                                {plan.firstPercent}
                                            </div>
                                        </div>
                                    </div>

                                    <p className="text-xs text-neutral-500 font-medium truncate border-b pb-2 border-dashed">
                                        {plan.description || "بدون توضیحات"}
                                    </p>

                                    <div
                                        className={cn(
                                            "w-full py-3 flex items-center justify-center gap-1 text-xs font-medium px-2 rounded-md",
                                            tag?.className ||
                                                "bg-neutral-50 text-neutral-600"
                                        )}
                                    >
                                        {tag?.Icon && (
                                            <Icon
                                                name={tag.Icon as string}
                                                size={16}
                                            />
                                        )}
                                        <span>{tag?.label || "نامشخص"}</span>
                                    </div>
                                </div>

                                <div className="w-36 h-24 shrink-0 rounded-xl bg-brand-950 overflow-hidden">
                                    <img
                                        className="w-full h-full object-cover"
                                        src={plan.imageUrl}
                                        alt={plan.title}
                                    />
                                </div>
                            </div>
                        );
                    })}

                {/* Empty */}
                {!isLoading &&
                    !isError &&
                    displayPlans.length === 0 && (
                        <div className="flex items-center justify-center py-8">
                            <div className="text-neutral-500">
                                هیچ طرحی یافت نشد
                            </div>
                        </div>
                    )}
            </div>
        </Modal>
    );
};

export default SelectPlanModal;